import React, { useState, useEffect, useRef } from 'react';
import AriaAvatar from './AriaAvatar';
import { submitAnswer, transcribeAudio, endSession, abandonSession } from '../services/api';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import './InterviewRoom.css';

const PHASES = {
  READY:      'ready',
  PAUSE:      'pause',
  RECORDING:  'recording',
  PROCESSING: 'processing',
  EVALUATING: 'evaluating',
  DONE:       'done',
};

export default function InterviewRoom({ sessionData, config, onRestart, onUpgrade }) {

  const [phase,         setPhase]         = useState(PHASES.READY);
  const [ariaState,     setAriaState]     = useState('speaking');
  const [currentQ,      setCurrentQ]      = useState(sessionData.questionNumber || 1);
  const [sessionId]                       = useState(sessionData.sessionId);
  const [ariaText,      setAriaText]      = useState(sessionData.message);
  const [interviewDone, setInterviewDone] = useState(false);
  const [log,           setLog]           = useState([
    { role: 'uru', text: sessionData.message, qNum: 1 }
  ]);
  const [error, setError] = useState('');

  // ── Plan info ──
  const [isFreePlan]          = useState(sessionData.isFreePlan || false);
  const [questionsPerSession] = useState(sessionData.questionsPerSession || 999);

  // ── Timer ──
  const [timeLeft,    setTimeLeft]    = useState(30 * 60);
  const [timerActive, setTimerActive] = useState(!sessionData.isFreePlan); // free plan doesn't use timer

  // ── Modals ──
  const [showWarning,        setShowWarning]        = useState(false);
  const [warningText,        setWarningText]        = useState('');
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const [abandoning,         setAbandoning]         = useState(false);

  const interviewDoneRef = useRef(false);
  const logRef           = useRef(null);
  const answerInputRef   = useRef(null);
  const timerIntervalRef = useRef(null);

  const { isRecording, audioBlob, duration, startRecording, stopRecording } = useAudioRecorder();
  const { playBase64Audio } = useAudioPlayer();

  // ── Format mm:ss ──
  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // ── Check free plan question limit ──
  const isFreePlanLimitReached = (questionIndex) => {
    return isFreePlan && questionIndex >= questionsPerSession;
  };

  // ── Handle ARIA response from backend ──
  const handleResponse = (data) => {
    addLog('uru', data.message, data.questionNumber);
    setAriaText(data.message);
    if (data.questionNumber) setCurrentQ(data.questionNumber);

    if (data.weakAnswerWarning) {
      setWarningText(data.weakAnswerWarning);
      setShowWarning(true);
    }

    if (data.status === 'COMPLETED') {
      setInterviewDone(true);
      interviewDoneRef.current = true;
      clearInterval(timerIntervalRef.current);
      setTimerActive(false);
      setPhase(PHASES.DONE);
      setAriaState('speaking');
      playBase64Audio(data.audioBase64, () => setAriaState('idle'));
    } else {
      setPhase(PHASES.READY);
      setAriaState('speaking');
      playBase64Audio(data.audioBase64, () => {
        setAriaState('idle');
        setPhase(PHASES.PAUSE);
      });
    }
  };

  // ── Core submit — handles both free limit and normal answers ──
  const submitToBackend = async (answerText, questionIndex) => {
    try {
      const isLastFreeQuestion = isFreePlanLimitReached(questionIndex);
      const answerPayload      = isLastFreeQuestion
        ? `FREE_LIMIT_${answerText}`
        : answerText;

      if (isLastFreeQuestion) {
        setPhase(PHASES.EVALUATING);
        setAriaState('thinking');
      }

      const { data } = await submitAnswer({
        sessionId,
        answer:        answerPayload,
        questionIndex: questionIndex,
      });
      handleResponse(data);
    } catch (e) {
      console.error(e);
      setError('Something went wrong. Please try again.');
      setPhase(PHASES.PAUSE);
      setAriaState('idle');
    }
  };

  // ── 30-min countdown (paid plans only) ──
  useEffect(() => {
    if (isFreePlan) return; // free plan ends by question count, not timer
    if (!timerActive) return;

    timerIntervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          setTimerActive(false);
          if (!interviewDoneRef.current) {
            setAriaState('thinking');
            setPhase(PHASES.EVALUATING);
            endSession({ sessionId })
              .then(({ data }) => {
                addLog('uru', data.message, 'Final');
                setAriaText(data.message);
                setInterviewDone(true);
                interviewDoneRef.current = true;
                setPhase(PHASES.DONE);
                setAriaState('speaking');
                playBase64Audio(data.audioBase64, () => setAriaState('idle'));
              })
              .catch(() => {
                setInterviewDone(true);
                interviewDoneRef.current = true;
                setPhase(PHASES.DONE);
                setAriaState('idle');
              });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerIntervalRef.current);
  }, [timerActive, isFreePlan]);

  // ── Auto-play initial audio ──
  useEffect(() => {
    if (sessionData.audioBase64) {
      setAriaState('speaking');
      setPhase(PHASES.READY);
      playBase64Audio(sessionData.audioBase64, () => {
        setAriaState('idle');
        setPhase(PHASES.PAUSE);
      });
    }
  }, []);

  // ── Page close → abandon ──
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!interviewDoneRef.current) {
        navigator.sendBeacon(
          '/api/interview/abandon',
          JSON.stringify({ sessionId })
        );
        e.preventDefault();
        e.returnValue = 'Interview in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sessionId]);

  // ── Auto-scroll log ──
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [log]);

  // ── Transcribe when blob ready ──
  useEffect(() => {
    if (audioBlob && phase === PHASES.PROCESSING) {
      handleTranscribeAndSubmit(audioBlob);
    }
  }, [audioBlob]);

  const addLog = (role, text, qNum) => {
    setLog(prev => [...prev, { role, text, qNum }]);
  };

  // ── Voice answer ──
  const handleTranscribeAndSubmit = async (blob) => {
    try {
      setAriaState('thinking');
      setError('');
      const { data: sttData } = await transcribeAudio(blob);
      const text = sttData.transcript?.trim();

      if (!text) {
        setError("Couldn't hear you clearly. Please try again.");
        setPhase(PHASES.PAUSE);
        setAriaState('idle');
        return;
      }

      addLog('candidate', text, currentQ);
      await submitToBackend(text, currentQ);

    } catch (e) {
      console.error(e);
      setError('Something went wrong. Please try again.');
      setPhase(PHASES.PAUSE);
      setAriaState('idle');
    }
  };

  // ── Typed answer ──
  const handleTypedSubmit = async () => {
    const val = answerInputRef.current?.value?.trim();
    if (!val) return;
    answerInputRef.current.value = '';
    addLog('candidate', val, currentQ);
    setPhase(PHASES.PROCESSING);
    setAriaState('thinking');
    await submitToBackend(val, currentQ);
  };

  const handleStartRecording = async () => {
    try {
      setError('');
      await startRecording();
      setPhase(PHASES.RECORDING);
      setAriaState('listening');
    } catch (e) {
      setError('Microphone access denied. Please allow microphone.');
    }
  };

  const handleStopRecording = () => {
    stopRecording();
    setPhase(PHASES.PROCESSING);
    setAriaState('thinking');
  };

  // ── End button ──
  const handleEndClick = () => {
    if (interviewDone) { onRestart(); return; }
    setShowAbandonConfirm(true);
  };

  // ── Abandon confirmed ──
  const handleAbandonConfirmed = async () => {
    setAbandoning(true);
    setShowAbandonConfirm(false);
    clearInterval(timerIntervalRef.current);
    setTimerActive(false);
    setAriaState('thinking');
    setPhase(PHASES.EVALUATING);

    try {
      const { data } = await abandonSession({ sessionId });
      if (data.message) {
        addLog('uru', data.message, 'Partial Eval');
        setAriaText(data.message);
        if (data.audioBase64) {
          setAriaState('speaking');
          playBase64Audio(data.audioBase64, () => setAriaState('idle'));
        } else {
          setAriaState('idle');
        }
      }
      setInterviewDone(true);
      interviewDoneRef.current = true;
      setPhase(PHASES.DONE);
    } catch (e) {
      setInterviewDone(true);
      interviewDoneRef.current = true;
      setPhase(PHASES.DONE);
      setAriaState('idle');
    } finally {
      setAbandoning(false);
    }
  };

  const isPartialEval = log.some(l => l.qNum === 'Partial Eval');

  return (
    <div className="room-root">

      {/* Background */}
      <div className="room-bg">
        <div className="room-orb orb-l" />
        <div className="room-orb orb-r" />
        <div className="grid-lines" />
      </div>

      {/* ── Abandon confirm modal ── */}
      {showAbandonConfirm && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">⚠️</div>
            <h3>End Interview Early?</h3>
            <p>
              Your session will be saved with a partial evaluation
              based on the questions you've answered so far.
            </p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-cancel"
                onClick={() => setShowAbandonConfirm(false)}>
                Continue Interview
              </button>
              <button className="modal-btn modal-btn-confirm"
                onClick={handleAbandonConfirmed}>
                End & Save Results
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Weak answer warning modal ── */}
      {showWarning && (
        <div className="modal-overlay">
          <div className="modal-card modal-warning">
            <div className="modal-icon">📚</div>
            <h3>A Friendly Suggestion</h3>
            <p>{warningText}</p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-cancel"
                onClick={() => { setShowWarning(false); handleAbandonConfirmed(); }}>
                End & Study First
              </button>
              <button className="modal-btn modal-btn-confirm"
                onClick={() => setShowWarning(false)}>
                Continue Trying
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <header className="room-header">
        <div className="room-brand">
          <span className="brand-dot" />
          <span className="room-brand-name">
            URU <span className="brand-sub">Interview</span>
          </span>
        </div>
        <div className="room-info">
          <span className="info-chip">{config.jobRole}</span>

          {/* Free plan: show question count */}
          {isFreePlan ? (
            <span className="info-chip timer-chip">
              Q {Math.min(currentQ, questionsPerSession)} / {questionsPerSession}
            </span>
          ) : (
            /* Paid plan: show countdown timer */
            <span className={`info-chip timer-chip
              ${timeLeft < 300 ? 'timer-warning' : ''}
              ${timeLeft < 60  ? 'timer-danger'  : ''}`}>
              🕐 {formatTime(timeLeft)}
            </span>
          )}

          <span className="info-chip">{config.experienceLevel}</span>
          <span className="info-chip">{config.candidateName}</span>
        </div>
        <button className="end-btn" onClick={handleEndClick}>
          {abandoning ? '...' : '✕ End'}
        </button>
      </header>

      {/* ── Progress bar ── */}
      <div className="progress-bar-wrap">
        {isFreePlan ? (
          <div
            className="progress-bar"
            style={{ width: `${((currentQ - 1) / questionsPerSession) * 100}%` }}
          />
        ) : (
          <div
            className={`progress-bar ${timeLeft < 300 ? 'bar-warning' : ''}`}
            style={{ width: `${((30 * 60 - timeLeft) / (30 * 60)) * 100}%` }}
          />
        )}
        <span className="progress-label">
          {interviewDone
            ? '✓ Complete'
            : isFreePlan
            ? `Question ${Math.min(currentQ, questionsPerSession)} of ${questionsPerSession}`
            : `${formatTime(timeLeft)} remaining`
          }
        </span>
      </div>

      {/* ── Main layout ── */}
      <div className="room-main">

        {/* Left: URU Avatar */}
        <div className="aria-panel">
          <div className="aria-scene">
            <div className="scene-glow" />
            <AriaAvatar state={ariaState} />
            <h3 className="aria-name">URU</h3>
            <p className="aria-title">AI Technical Interviewer</p>
          </div>
          <div className={`speech-bubble ${ariaState === 'speaking' ? 'active' : ''}`}>
            <div className="speech-content">{ariaText}</div>
          </div>
        </div>

        {/* Right: Conversation + Controls */}
        <div className="interaction-panel">

          {/* Free plan upgrade banner */}
          {isFreePlan && !interviewDone && (
            <div className="free-plan-banner">
              <span>🎁 Free Plan — {questionsPerSession} questions only</span>
              {/* ✅ Fixed: use onUpgrade prop, not setShowPaywall */}
              <button onClick={onUpgrade}>
                Upgrade for full 30-min interviews →
              </button>
            </div>
          )}

          {/* Conversation log */}
          <div className="convo-log" ref={logRef}>
            {log.map((entry, i) => (
              <div key={i} className={`log-entry log-${entry.role}`}>
                <div className="log-avatar">
                  {entry.role === 'uru' ? '🤖' : '👤'}
                </div>
                <div className="log-body">
                  <span className="log-name">
                    {entry.role === 'uru' ? 'URU' : config.candidateName}
                    {entry.role === 'uru' && entry.qNum && (
                      <span className="log-qtag">Q{entry.qNum}</span>
                    )}
                  </span>
                  <p className="log-text">{entry.text}</p>
                </div>
              </div>
            ))}

            {phase === PHASES.PROCESSING && (
              <div className="log-entry log-uru">
                <div className="log-avatar">🤖</div>
                <div className="log-body">
                  <span className="log-name">URU</span>
                  <div className="typing-indicator">
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}

            {phase === PHASES.EVALUATING && (
              <div className="log-entry log-uru">
                <div className="log-avatar">🤖</div>
                <div className="log-body">
                  <span className="log-name">URU</span>
                  <p className="log-text"
                    style={{ color: 'var(--warning)', fontStyle: 'italic' }}>
                    ⏱ Preparing your evaluation...
                  </p>
                </div>
              </div>
            )}
          </div>

          {error && <div className="error-banner">{error}</div>}

          {/* Controls */}
          {!interviewDone && (
            <div className="controls-area">

              {phase === PHASES.READY && (
                <div className="status-msg">
                  <div className="status-dot speaking-dot" />
                  URU is speaking...
                </div>
              )}

              {phase === PHASES.PAUSE && (
                <div className="answer-zone">
                  <p className="answer-prompt">
                    Your turn — speak your answer or type it below
                  </p>
                  <div className="answer-actions">
                    <button className="mic-btn" onClick={handleStartRecording}>
                      <span className="mic-icon">🎤</span>
                      <span>Speak Answer</span>
                    </button>
                    <span className="or-divider">or</span>
                    <div className="type-zone">
                      <textarea
                        ref={answerInputRef}
                        placeholder="Type your answer here..."
                        rows={3}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && e.ctrlKey) handleTypedSubmit();
                        }}
                      />
                      <button className="send-btn" onClick={handleTypedSubmit}>
                        Send ➤
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {phase === PHASES.RECORDING && (
                <div className="recording-zone">
                  <div className="recording-indicator">
                    <div className="rec-dot" />
                    <span>Recording — {duration}s</span>
                    <div className="live-bars">
                      {[...Array(8)].map((_, i) => (
                        <div key={i} className="live-bar"
                          style={{ animationDelay: `${i * 0.1}s` }} />
                      ))}
                    </div>
                  </div>
                  <button className="stop-btn" onClick={handleStopRecording}>
                    ⏹ Stop Recording
                  </button>
                </div>
              )}

              {(phase === PHASES.PROCESSING || phase === PHASES.EVALUATING) && (
                <div className="status-msg">
                  <div className="spinner-sm" />
                  {phase === PHASES.EVALUATING
                    ? 'Generating your evaluation...'
                    : 'Processing your answer...'}
                </div>
              )}

            </div>
          )}

          {/* Done panel */}
          {interviewDone && (
            <div className="done-panel">
              <div className="done-icon">{isPartialEval ? '📋' : '🎉'}</div>
              <h3>{isPartialEval ? 'Session Saved' : 'Interview Complete!'}</h3>
              <p>
                {isPartialEval
                  ? 'Your partial evaluation has been saved to your history.'
                  : 'URU has evaluated your performance. Check the evaluation above.'
                }
              </p>
              {/* Show upgrade CTA for free plan users */}
              {isFreePlan && onUpgrade && (
                <button className="upgrade-done-btn" onClick={onUpgrade}>
                  🚀 Upgrade for Full 30-Min Interviews
                </button>
              )}
              <button className="restart-btn" onClick={onRestart}>
                Back to Home
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}